import { useState, useMemo } from "react";

export const useDomesticCountryForm = (localized) => {
  // Regex patterns for display name validation
  const DISPLAY_NAME_VALID_CHARS_REGEX = /^[a-zA-Z\s\-\(\)]+$/;
  const CONSECUTIVE_SPACES_REGEX = /\s{2,}/;

  // Form state
  const [countryCode, setCountryCode] = useState("");
  const [displayName, setDisplayName] = useState("");

  // Validation state
  const [countryCodeTouched, setCountryCodeTouched] = useState(false);
  const [displayNameTouched, setDisplayNameTouched] = useState(false);

  // Error state
  const [dialogError, setDialogError] = useState("");

  // Validation functions
  const validateCountryCode = (code) => {
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      return localized.DomesticCountriesRequiredCountry;
    }

    if (trimmedCode.length !== 2) {
      return localized.DomesticCountriesIOSFormat;
    }

    if (!/^[A-Z]{2}$/.test(trimmedCode)) {
      return localized.DomesticCountriesFormatUpperCase;
    }

    return null;
  };

  const validateDisplayName = (name) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return localized.DomesticCountriesNameRequired;
    }

    if (trimmedName.length < 2) {
      return localized.DomesticCountriesNameMinLength;
    }

    if (trimmedName.length > 100) {
      return localized.DomesticCountriesNameMaxLength;
    }

    if (!DISPLAY_NAME_VALID_CHARS_REGEX.test(trimmedName)) {
      return localized.DomesticCountriesInvalidCharacters;
    }

    if (CONSECUTIVE_SPACES_REGEX.test(trimmedName)) {
      return localized.DomesticCountriesWhiteSpaces;
    }

    return null;
  };

  const handleCountryCodeChange = (event) => {
    setCountryCode(event.target.value.toUpperCase());
  };

  const handleDisplayNameChange = (event) => {
    setDisplayName(event.target.value);
  };

  const handleCountryCodeBlur = () => {
    setCountryCodeTouched(true);
  };

  const handleDisplayNameBlur = () => {
    setDisplayNameTouched(true);
  };

  const formIsValid = useMemo(() => {
    const countryCodeError = validateCountryCode(countryCode);
    const displayNameError = validateDisplayName(displayName);

    return !countryCodeError && !displayNameError;
  }, [countryCode, displayName]);

  const getValidationErrors = () => {
    const errors = {};

    if (countryCodeTouched) {
      const countryCodeError = validateCountryCode(countryCode);
      if (countryCodeError) {
        errors.countryCode = countryCodeError;
      }
    }

    if (displayNameTouched) {
      const displayNameError = validateDisplayName(displayName);
      if (displayNameError) {
        errors.displayName = displayNameError;
      }
    }

    return errors;
  };

  const resetForm = () => {
    setCountryCode("");
    setDisplayName("");
    setCountryCodeTouched(false);
    setDisplayNameTouched(false);
    setDialogError("");
  };

  const populateForm = (row) => {
    setCountryCode(row.countryCode);
    setDisplayName(row.displayName);
    setCountryCodeTouched(false);
    setDisplayNameTouched(false);
    setDialogError("");
  };

  const getFormData = (id = null) => {
    return {
      id,
      countryCode: countryCode.trim(),
      displayName: displayName.trim(),
    };
  };

  return {
    // Form state
    countryCode,
    displayName,

    // Validation state
    countryCodeTouched,
    displayNameTouched,
    dialogError,

    // Handlers
    handleCountryCodeChange,
    handleDisplayNameChange,
    handleCountryCodeBlur,
    handleDisplayNameBlur,

    // Validation functions
    validateCountryCode,
    validateDisplayName,
    getValidationErrors,

    // Utilities
    isFormValid: formIsValid,
    resetForm,
    populateForm,
    getFormData,
    setDialogError,
  };
};
